import ltng from '../api/ltng'
import resources from '../api/resources'

export default function (ctx, inject) {
  // V2 client — the modules managed from the V3 manager (CMS pages + Transact
  // products), read from the V2 account via the public routers.
  const ltngV2Client = ctx.$axios.create({
    baseURL: ctx.$config.ltngApi,
    params: {
      account_id: ctx.$config.ltngId,
    },
  })

  // V1 (legacy) client — everything else keeps talking to the original V1
  // account, which works with the site user's token and has the modules the V2
  // account isn't provisioned for. See api/ltng/index.js for the split.
  const ltngV1Client = ctx.$axios.create({
    baseURL: ctx.$config.ltngV1Api,
    params: {
      account_id: ctx.$config.ltngV1Id,
    },
  })

  const resourcesClient = ctx.$axios.create({
    baseURL: ctx.$config.resourcesApi,
  })

  const ltngApi = ltng(ltngV2Client, ltngV1Client)
  const resourcesApi = resources(resourcesClient)

  if (ctx.$auth.loggedIn) {
    const token = ctx.$auth.strategy.token.get()
    resourcesClient.setToken(token)
    ltngV2Client.setToken(token)
    ltngV1Client.setToken(token)
  }

  // The 401-refresh interceptor lives on the V1 client: that is where the
  // authenticated calls (orders, customers, users, access-requests…) run. The
  // V2 client only does anonymous public reads, so it never 401s on auth.
  ltngV1Client.interceptors.response.use(
    (response) => response,
    async function (error) {
      const originalRequest = error.config

      if ([401].includes(error.response.status) && !originalRequest._retry) {
        originalRequest._retry = true

        try {
          const { id_token: token } = await refreshHandler(ltngV1Client, ctx)

          ltngApi.analytics.record({
            auth: 'extension',
          })

          await Promise.all([
            ctx.$auth.strategy.token.set(token),
            ctx.$api.setToken(token),
          ])

          originalRequest.headers.Authorization = `Bearer ${token}`

          return ltngV1Client(originalRequest)
        } catch (error) {
          if (ctx.$auth.loggedIn) {
            ctx.store.dispatch('user/logout')
          }
        }
      }

      return Promise.reject(error)
    }
  )

  inject('api', {
    ltng: ltngApi,
    resources: resourcesApi,

    setToken: async (token) => {
      return await Promise.all([
        resourcesClient.setToken(token, 'Bearer'),
        ltngV2Client.setToken(token, 'Bearer'),
        ltngV1Client.setToken(token, 'Bearer'),
      ])
    },
  })
}

async function refreshHandler(axiosClient, context) {
  try {
    const { data } = await axiosClient.post(
      `https://${context.$config.auth0.domain}/oauth/token`,
      {
        grant_type: 'refresh_token',
        client_id: context.$config.auth0.clientId,
        refresh_token: context.$auth.strategy.refreshToken.get(),
      }
    )

    return data
  } catch (error) {
    return Promise.reject(error)
  }
}
